import React from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, RouteIcon } from '@hero/web-icons';
import { useRouteListApi } from '@hero/api-hooks';
import { useNav } from '@hero/web-hooks';
import { useCurrentUser } from '@hero/hooks-shared';
import { getObjectId } from '@hero/schema';
import type { Agreement } from '@hero/schema';
import { Button } from '../../../ui/Button';
import { RoutePriceCard } from '../../Route/Card/RoutePriceCard';
import { getAgreementCounterparty } from '../utils';

export interface AgreementRoutesTabProps {
  agreement: Agreement;
  /**
   * Closes the parent agreement drawer. Called right before navigating
   * to the client workspace's Pricing tab so the drawer doesn't sit on
   * top of the destination sheet.
   */
  onCloseDrawer: () => void;
}

/**
 * Routes scoped to a single agreement. Read-only summary list — clicking
 * "Add route" or any existing route navigates the user to the client
 * workspace's Pricing tab (`/crm?clientId=…&tab=pricing&agreementId=…`)
 * where the spreadsheet-style `PricingSheetPage` owns all create / edit /
 * delete flows. The legacy `RouteEditModal` and per-route `RouteDrawer`
 * are intentionally not mounted here anymore — pricing and routes both
 * live on the Pricing tab now, with one source of truth.
 */
export function AgreementRoutesTab({
  agreement,
  onCloseDrawer,
}: AgreementRoutesTabProps) {
  const { t } = useTranslation(['m-agreement', 'm-route']);
  const { orgId } = useCurrentUser();
  const { navigate } = useNav();

  const { data: routes = [], isLoading } = useRouteListApi({
    agreementId: getObjectId(String(agreement._id)),
  });

  const counterparty = getAgreementCounterparty(agreement, orgId);
  const counterpartyClientId = counterparty
    ? String(counterparty._id)
    : undefined;

  // Hand-off: close the drawer first so the destination sheet isn't
  // covered by it, then jump to the pricing tab seeded with this
  // agreement's id (`ClientPricingTab` builds the agreement scope from
  // the URL param).
  const goToPricing = () => {
    if (!counterpartyClientId) return;
    onCloseDrawer();
    navigate('crm', {
      clientId: counterpartyClientId,
      tab: 'pricing',
      agreementId: String(agreement._id),
    });
  };

  return (
    <div className="px-5 py-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">
            {t('drawer.routes_section.title')}
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {t('drawer.routes_section.description')}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          className="gap-1.5 flex-shrink-0"
          disabled={!counterpartyClientId}
          onClick={goToPricing}>
          <PlusIcon className="size-3.5" />
          {t('drawer.routes_section.add_route')}
        </Button>
      </div>

      {isLoading && routes.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <div className="size-6 rounded-full border-2 border-gray-200 border-t-primary animate-spin" />
        </div>
      ) : routes.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <div className="size-10 rounded-full bg-white flex items-center justify-center border">
            <RouteIcon className="size-5 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {t('drawer.routes_section.empty_title', { defaultValue: 'No routes yet' })}
            </p>
            <p className="text-xs text-gray-500 mt-0.5 max-w-xs">
              {t('drawer.routes_section.empty_description', {
                defaultValue:
                  'Attach directed lane rates so the resolver can auto-price orders that fall under this agreement.',
              })}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!counterpartyClientId}
            onClick={goToPricing}>
            <PlusIcon className="size-3.5" />
            {t('drawer.routes_section.add_route')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {routes.map(r => (
            <RoutePriceCard key={String(r._id)} route={r} onClick={goToPricing} />
          ))}
        </div>
      )}
    </div>
  );
}
